import { forwardRef, memo } from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

const ButtonStyled = styled(Button)(({ theme }) => ({
	boxShadow: "none",
	"&.MuiButton-black-text:hover, &.MuiButton-text:hover, &.MuiButton-green-text:hover, &.MuiButton-red-text:hover": {
		backgroundColor: "transparent",
		transform: "scale(1.15)",
		transition: "transform 0.1s ease",
	},
}));

const BaseButton = memo(
	forwardRef(({ customclasses = "", className = "", issubmitting, disabled, ...props }, ref) => {
		let variant = "";
		let style = "";
		let isDisabled = disabled || issubmitting ? "disabled" : "";

		let customstyles = { ...props.customstyles };

		switch (props.variant) {
			case "outlined":
				variant = "outlined";
				style = `font-size-xm bg-white ${props.fontColor ? props.fontColor : "color-primary outline-primary"}`;
				if (props.fontColor) {
					const colorValue = `var(--theme-${props.fontColor.replace("color-", "")})`;
					customstyles.borderColor = colorValue;
					customstyles.color = colorValue;
				}
				break;
			case "abort":
				variant = "outlined";
				style = "font-size-xm color-secondary bg-white outline-secondary";
				break;
			case "undo":
				variant = "outlined";
				style = "font-size-xm color-black bg-senary";
				break;
			case "small":
				variant = "outlined";
				style = "font-size-xm theme-base-button-sm bg-senary";
				break;
			case "medium":
				variant = "outlined";
				style = "font-size-xm theme-base-button-md color-white bg-primary";
				break;
			case "primary":
				variant = "contained";
				style = "bg-primary color-white font-size-xm";
				break;
			case "red":
			case "green":
				variant = "contained";
				style = "bg-" + props.variant + " color-white font-size-xm";
				break;
			case "text":
				variant = "text";
				style = "color-primary color-primary-hover";
				break;
			case "black-text":
				variant = "text";
				style = "color-black";
				break;
			case "white-text":
				variant = "text";
				style = "color-white";
				break;
			case "green-text":
				variant = "text";
				style = "color-green";
				break;
			case "red-text":
				variant = "text";
				style = "color-red";
				break;
			case "quick-access-text":
				variant = "text";
				style = "color-white";
				break;
			default:
				variant = "contained";
				style = "bg-nonary color-primary font-size-xm";
		}

		const mergedClassNames = `theme-base-button font-primary ${style} ${isDisabled} ${customclasses} ${className}`;

		return (
			<ButtonStyled
				ref={ref}
				style={{ textTransform: "none", ...customstyles }}
				className={mergedClassNames}
				variant={variant}
				disabled={disabled || issubmitting}
				sx={{
					"&.Mui-disabled": {
						...(variant === "contained" && {
							color: "var(--theme-quaternary) !important",
						}),
					},
				}}
				{...props}
			>
				{issubmitting && <CircularProgress className="me-2" sx={{ color: "var(--theme-septenary)" }} size={20} thickness={7} />}
				{props.children}
			</ButtonStyled>
		);
	})
);

BaseButton.displayName = "BaseButton";

export default BaseButton;
